import { useEffect, useRef, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
  authClient,
  companyInviteTokenFromPath,
  navigateSafeReturn,
  roleHome,
  sendEmployerVerificationOtp,
} from "@/lib/auth-client"
import { Logo } from "@/components/logo"
import { useEmployerVerifyProfile } from "@/features/company"
import { toast } from "sonner"
import { errorMessage } from "@/lib/errors"

const RESEND_COOLDOWN_S = 60

export const Route = createFileRoute("/recruit_/verify")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: RecruitVerifyPage,
})

/**
 * Employer work-email OTP step. Email/password recruiter signups land here
 * before a profile exists; Google signups never see it (already verified).
 * The server refuses employer profile creation and every employer operation
 * until the auth user's email is verified.
 */
function RecruitVerifyPage() {
  const { returnTo } = Route.useSearch()
  const navigate = useNavigate()
  const { data: session, isPending } = authClient.useSession()
  const { profile, ensureProfile } = useEmployerVerifyProfile()
  const [otp, setOtp] = useState("")
  const [busy, setBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const sentRef = useRef(false)
  const finishedRef = useRef(false)

  const email = session?.user?.email ?? ""

  const finish = async () => {
    if (finishedRef.current) return
    finishedRef.current = true
    try {
      if (profile) {
        navigateSafeReturn(navigate, returnTo, roleHome(profile.role))
        return
      }
      const { claimedCompany } = await ensureProfile({
        role: "employer",
        name: session?.user?.name || undefined,
        inviteToken: companyInviteTokenFromPath(returnTo) ?? undefined,
      })
      if (claimedCompany) {
        void navigate({ to: roleHome("employer") })
        return
      }
      void navigate({
        to: "/onboarding/company",
        search: returnTo ? { returnTo } : {},
      })
    } catch (err) {
      finishedRef.current = false
      toast.error(errorMessage(err, "Failed to finish signup"))
    }
  }

  // Not signed in — the code belongs to a signup that no longer exists here.
  useEffect(() => {
    if (!isPending && !session?.user) {
      void navigate({ to: "/recruit/join", search: returnTo ? { returnTo } : {} })
    }
  }, [isPending, session, navigate, returnTo])

  // Already verified (Google, or a refresh after verifying) — continue on.
  useEffect(() => {
    if (session?.user?.emailVerified && profile !== undefined) {
      void finish()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.emailVerified, profile])

  // First unverified visit: send the code once, then let the user resend.
  useEffect(() => {
    if (!session?.user || session.user.emailVerified || sentRef.current) return
    sentRef.current = true
    setCooldown(RESEND_COOLDOWN_S)
    void sendEmployerVerificationOtp(session.user.email).then((ok) => {
      if (!ok) toast.error("Couldn't send the code — use Resend to try again")
    })
  }, [session])

  useEffect(() => {
    if (cooldown <= 0) return
    const t = window.setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [cooldown])

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (otp.trim().length !== 6) return
    setBusy(true)
    try {
      const res = await authClient.emailOtp.verifyEmail({
        email,
        otp: otp.trim(),
      })
      if (res.error) {
        throw new Error(res.error.message ?? "That code didn't match")
      }
      toast.success("Work email verified")
      await finish()
    } catch (err) {
      toast.error(errorMessage(err, "That code didn't match"))
    } finally {
      setBusy(false)
    }
  }

  const onResend = async () => {
    if (cooldown > 0 || !email) return
    setCooldown(RESEND_COOLDOWN_S)
    const ok = await sendEmployerVerificationOtp(email)
    if (ok) toast.success("New code sent")
    else toast.error("Couldn't send the code — try again in a minute")
  }

  return (
    <div className="mkt-auth home-wf">
      <Link to="/" className="home-wf-brand" aria-label="Hire home">
        <Logo />
      </Link>
      <div className="mkt-auth-card">
        <div className="mkt-auth-panel">
          <header className="mkt-auth-head">
            <h1 className="mkt-auth-title">Verify your work email</h1>
            <p className="mkt-auth-desc">
              Hiring on Hire is tied to your company work email. We sent a
              6-digit code{email ? ` to ${email}` : ""} — enter it to prove the
              inbox is yours.
            </p>
            <p className="mkt-auth-proof" aria-label="Verification facts">
              <span>Work email required</span>
              <span>Code expires in 10 min</span>
            </p>
          </header>

          <form className="mkt-auth-actions" onSubmit={onVerify}>
            <div className="mkt-auth-field">
              <label htmlFor="otp">Verification code</label>
              <input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="mkt-auth-submit"
              disabled={busy || otp.length !== 6}
            >
              {busy ? "Verifying…" : "Verify and continue"}
            </button>

            <div className="mkt-auth-foot">
              <p>
                Didn't get it?{" "}
                <button
                  type="button"
                  className="underline underline-offset-4 disabled:no-underline disabled:opacity-60"
                  onClick={() => void onResend()}
                  disabled={cooldown > 0}
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
